import React, { useEffect, useMemo, useState, useRef } from "react";
import { Form, Input } from "antd";
import type { FormInstance } from "antd";
import { useTranslation } from "react-i18next";
import { PaymentMethodType } from "types/paymentMethodTypes";
import CustomFileUpload from "components/Common/ui/Files/CustomFileUpload";

type Props = {
  form: FormInstance;
  paymentMethods: PaymentMethodType[];
  paymentMethodIdField?: string;
  i18nBase?: string;
  accept?: string;
  maxCount?: number;
  maxSizeMB?: string | number;
  hasExistingTransferProof?: boolean;
  existingTransferProofUrl?: string | null;
};

const PAYMENT_METHOD_TYPES = {
  CASH: "cash",
  CHECK: "bank_check",
  BANK_TRANSFER: "bank_transfer",
} as const;

const PaymentMethodExtraFields: React.FC<Props> = ({
  form,
  paymentMethods,
  paymentMethodIdField = "paymentMethodId",
  i18nBase = "paymentMethod",
  accept = ".pdf,.jpg,.jpeg,.png",
  maxCount = 1,
  maxSizeMB,
  hasExistingTransferProof = false,
  existingTransferProofUrl = null,
}) => {
  const { t } = useTranslation();

  const [transferProofFiles, setTransferProofFiles] = useState<File[]>([]);
  const [isExistingTransferProofDeleted, setIsExistingTransferProofDeleted] =
    useState(false);

  const paymentMethodId = Form.useWatch(paymentMethodIdField, form);

  const selected = useMemo(
    () => paymentMethods?.find((m) => m.id === paymentMethodId),
    [paymentMethods, paymentMethodId]
  );

  const flags = useMemo(() => {
    if (!selected?.type) {
      return {
        isCheque: false,
        isTransfer: false,
        showBankName: false,
      };
    }

    return {
      isCheque: selected.type === PAYMENT_METHOD_TYPES.CHECK,
      isTransfer: selected.type === PAYMENT_METHOD_TYPES.BANK_TRANSFER,
      showBankName: selected.type === PAYMENT_METHOD_TYPES.CHECK,
    };
  }, [selected?.type]);

  const prevPaymentMethodId = useRef<string | undefined>(undefined);

  const numericMaxSizeMB =
    typeof maxSizeMB === "string" ? Number(maxSizeMB) : maxSizeMB;

  // Existing backend file only counts if present AND user did not delete it in this session
  const effectiveHasExistingTransferProof =
    hasExistingTransferProof && !isExistingTransferProofDeleted;

  useEffect(() => {
    if (prevPaymentMethodId.current === undefined) {
      prevPaymentMethodId.current = paymentMethodId;
      return;
    }

    if (prevPaymentMethodId.current === paymentMethodId) return;

    prevPaymentMethodId.current = paymentMethodId;

    if (!paymentMethodId) {
      form.setFieldsValue({
        bankName: null,
        chequeReference: null,
        transferProof: null,
      });
      setTransferProofFiles([]);
      setIsExistingTransferProofDeleted(false);
      return;
    }

    if (!flags.isCheque) {
      form.setFieldsValue({ chequeReference: null });
    }
    if (!flags.isTransfer) {
      form.setFieldsValue({
        transferProof: null,
      });
      setTransferProofFiles([]);
      setIsExistingTransferProofDeleted(false);
    }
    if (!flags.showBankName) {
      form.setFieldsValue({ bankName: null });
    }
  }, [
    paymentMethodId,
    flags.isCheque,
    flags.isTransfer,
    flags.showBankName,
    form,
  ]);

  if (!paymentMethodId) return null;

  const handleTransferProofChange = (files: File[]) => {
    setTransferProofFiles(files);
    form.setFieldsValue({
      transferProof: files[0] ?? null,
    });
   
  };

  const handleRemoveExistingTransferProof = () => {
    setIsExistingTransferProofDeleted(true);
    form.setFieldsValue({
      transferProof: null,
    });
    setTransferProofFiles([]);
  };

  return (
    <>
      {/* BANK NAME */}
      <Form.Item
        hidden={!flags.showBankName}
        label={t(`${i18nBase}.form.bank_name`)}
        name="bankName"
        rules={[
          {
            required: flags.showBankName,
            message: t(`${i18nBase}.validation.please_enter_bank_name`),
          },
        ]}
      >
        <Input placeholder={t(`${i18nBase}.form.bank_name`)} />
      </Form.Item>

      {/* CHEQUE REFERENCE */}
      <Form.Item
        hidden={!flags.isCheque}
        label={t(`${i18nBase}.form.cheque_reference`)}
        name="chequeReference"
        rules={[
          {
            required: flags.isCheque,
            message: t(
              `${i18nBase}.validation.please_enter_cheque_reference`
            ),
          },
        ]}
      >
        <Input placeholder={t(`${i18nBase}.form.cheque_reference`)} />
      </Form.Item>

      {/* Hidden transferProof field – real validation lives here */}
      <Form.Item
        hidden
        name="transferProof"
        rules={[
          {
            validator: () => {
              const hasNewFile = transferProofFiles.length > 0;
              const hasExisting = effectiveHasExistingTransferProof;
              if (flags.isTransfer && !hasNewFile && !hasExisting) {
                return Promise.reject(
                  t(`${i18nBase}.validation.please_upload_transfer_proof`)
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input type="hidden" />
      </Form.Item>

      {/* Mirror hidden field errors into visible upload field */}
      <Form.Item
        shouldUpdate={(prev, curr) =>
          prev.transferProof !== curr.transferProof ||
          prev.paymentMethodId !== curr.paymentMethodId
        }
        noStyle
      >
        {({ getFieldError }) => {
          const errors = getFieldError("transferProof");

          return (
            <Form.Item
              hidden={!flags.isTransfer}
              label={t(`${i18nBase}.form.transfer_proof`)}
              validateStatus={errors.length ? "error" : ""}
              help={errors[0]}
            >
              <CustomFileUpload
                multiple={false}
                maxCount={maxCount}
                maxSizeMB={numericMaxSizeMB}
                accept={accept}
                value={transferProofFiles}
                onChange={handleTransferProofChange}
                existingFiles={
                  effectiveHasExistingTransferProof && existingTransferProofUrl
                    ? [existingTransferProofUrl]
                    : []
                }
                onRemoveExisting={handleRemoveExistingTransferProof}
                showUploadList
                listType="text"
              />
            </Form.Item>
          );
        }}
      </Form.Item>
    </>
  );
};

export default PaymentMethodExtraFields;
